import React, { FC, useEffect } from 'react';
import { Button, DatePicker, Form, InputNumber, Modal } from 'antd';
import locale from 'antd/es/date-picker/locale/ru_RU';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { disabledDate } from 'common/helpers/date.helper';
import { rules } from 'common/helpers/form.helper';
import { IFormValue, ISupplyShort } from 'common/models';
import { DEFAULT_VALUE_1 } from 'common/config';
import { ReactComponent as CalendarIcon } from 'app/assets/images/redesign/calendar.svg';
import { ReactComponent as ArrowLeftShortIcon } from 'app/assets/images/redesign/arrow-left-short.svg';
import { ReactComponent as ArrowRightShortIcon } from 'app/assets/images/redesign/arrow-right-short.svg';

interface IComponentProps {
  open: boolean;
  supplies: ISupplyShort[];
  canSplitSupply: boolean;
  loading: boolean;
  onClose: () => void;
  onApply: (deliveryDate: string, count: number, onSuccess: () => void) => void;
}

export const RequestSupplyChangeDeliveryDateModal: FC<IComponentProps> = (props) => {
  const { open, supplies, canSplitSupply, loading, onClose, onApply } = props;

  const [form] = useForm();

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose();
    form.resetFields();
  };

  const onSubmit = ({ deliveryDate, count }: IFormValue) => {
    const formattedDeliveryDate = dayjs(deliveryDate).format('YYYY-MM-DD');

    onApply(formattedDeliveryDate, count, handleClose);
  };

  useEffect(() => {
    if (open && canSplitSupply) {
      form.setFieldsValue({
        count: supplies[0].count,
      });
    }
  }, [open, canSplitSupply, supplies]);

  return (
    <Modal
      rootClassName="redesign modal"
      width={420}
      centered
      open={open}
      onCancel={handleClose}
      destroyOnClose
      closeIcon={false}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleClose} loading={loading}>
          Отмена
        </Button>,
        <Button key="apply" className="button-sm primary" onClick={form.submit} loading={loading}>
          Применить
        </Button>,
      ]}
    >
      <div className="text-h4 mb-6">Выберите дату доставки</div>

      <div className="text-tag color-dark-grey mb-6">Укажите дату, к которой сможете доставить товар</div>

      <Form form={form} requiredMark={false} layout="vertical" onFinish={onSubmit}>
        <Form.Item label="Срок доставки" name="deliveryDate" rules={[rules.required()]}>
          <DatePicker
            popupStyle={{ width: 380 }}
            placeholder="Выберите дату доставки"
            suffixIcon={<CalendarIcon className="icon-calendar-dark-grey" />}
            allowClear={false}
            showToday={false}
            inputReadOnly
            locale={locale}
            format="DD.MM.YYYY (dd)"
            disabledDate={disabledDate}
            prevIcon={<ArrowLeftShortIcon />}
            nextIcon={<ArrowRightShortIcon />}
          />
        </Form.Item>

        {canSplitSupply && (
          <>
            <Form.Item
              label="Количество"
              name="count"
              rules={[
                rules.required(),
                () => ({
                  validator(_, value) {
                    if (value < DEFAULT_VALUE_1) {
                      return Promise.reject(new Error('Количество товаров не должно быть меньше 1'));
                    }

                    if (value > supplies[0].count) {
                      return Promise.reject(new Error(`Количество товаров не должно быть больше ${supplies[0].count}`));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber placeholder="Введите количество" suffix="шт" controls={false} precision={0} />
            </Form.Item>

            <div className="text-tag color-dark-grey mb-6">
              Если можете привезти только часть заказа, измените количество товаров. Остальная часть останется без назначенной
              даты поставки.
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};
